import React from 'react';
import classNames from 'classnames';
import _isEmpty from 'lodash/isEmpty';
import { CInput, CLabel, CSpinner } from '@coreui/react';
import PropTypes from 'prop-types';
import PlacesAutocomplete from 'react-places-autocomplete';
import { getFullAddressInfo, placeRequest } from './helper';

import './styles.scss';

const AddressSearch = ({
  placeholder,
  error,
  onSelect,
  onChange,
  label,
  value,
  invalid,
  disabled,
  filterSuggestionsByState,
}) => {
  const [address, setAddress] = React.useState('');
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [changed, setChanged] = React.useState(false);

  const handleChange = (val) => {
    setAddress(val);
    setChanged(true);
  };

  React.useEffect(() => {
    setAddress(value || '');
  }, [value]);

  const onBlur = () => {
    onChange(address);
    setShowSuggestions(false);
  };

  const handleSelect = (val, id) => {
    const request = {
      placeId: id,
      fields: ['address_components', 'geometry'],
    };
    setChanged(false);
    placeRequest(request).then(({ address_components: addressComponents, geometry }) => {
      onSelect({
        ...getFullAddressInfo(addressComponents),
        lat: geometry?.location.lat(),
        lng: geometry?.location.lng(),
      });
    });
  };

  const shouldFetchSuggestions = React.useMemo(() => address.trim().length >= 5, [address]);

  const filtredSuggestions = (suggestions) =>
    suggestions.filter(({ description }) =>
      filterSuggestionsByState ? description.includes(filterSuggestionsByState) : true,
    );

  return (
    <PlacesAutocomplete
      value={address}
      onChange={handleChange}
      onSelect={handleSelect}
      shouldFetchSuggestions={shouldFetchSuggestions}
      debounce={300}
      searchOptions={{
        componentRestrictions: {
          country: ['us'],
        },
        addressComponets: {
          street_number: 'short_name',
          route: 'long_name',
          locality: 'long_name',
          administrative_area_level_1: 'long_name',
          country: 'long_name',
          postal_code: 'short_name',
        },
      }}
      type="address"
      disabled={disabled}
    >
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
        <div className={classNames(['address', invalid && 'is-invalid'])}>
          {label && (
            <CLabel htmlFor="address-search" className="mb-2 d-block">
              {label}
            </CLabel>
          )}
          <div className="position-relative">
            <CInput
              {...getInputProps({
                placeholder,
                id: 'address-search',
                className: 'px-3 py-3',
                onFocus: () => setShowSuggestions(true),
                onBlur,
                disabled,
              })}
              invalid={!!error}
            />
            {loading && <CSpinner className="address__loading" size="sm" />}
          </div>
          <p className="text-red-500 text-xs italic mt-1 error">{error}</p>
          {showSuggestions && (
            <div className="address-results">
              {changed && shouldFetchSuggestions && !loading && (
                <div className="address-result__close">
                  <strong>✕</strong>
                </div>
              )}
              {_isEmpty(filtredSuggestions(suggestions)) &&
                changed &&
                shouldFetchSuggestions &&
                !loading && <div className="no-suggestions">No suggestions</div>}
              {filtredSuggestions(suggestions).map((suggestion, index) => {
                const className = suggestion.active
                  ? 'address-results__variant'
                  : 'address-results__variant';
                // inline style for demonstration purpose
                const style = suggestion.active
                  ? {
                      backgroundColor: 'var(--secondary)',
                      cursor: 'pointer',
                      transform: 'none !important',
                    }
                  : {
                      backgroundColor: 'var(--light)',
                      cursor: 'pointer',
                    };
                return (
                  <div
                    {...getSuggestionItemProps(suggestion, {
                      className,
                      style,
                    })}
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${suggestion.id}${index}`}
                    title={suggestion.description}
                  >
                    {suggestion.description}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </PlacesAutocomplete>
  );
};

AddressSearch.propTypes = {
  placeholder: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  label: PropTypes.string,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  filterSuggestionsByState: PropTypes.string,
  invalid: PropTypes.bool,
};

AddressSearch.defaultProps = {
  placeholder: '',
  error: '',
  onChange: null,
  onSelect: () => {},
  label: null,
  value: null,
  disabled: false,
  filterSuggestionsByState: null,
  invalid: false,
};

export default AddressSearch;
